import React, { useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { CompanyLogo } from '../../../../../components/CompanyLogo';
import { CompanyName } from '../../../../../components/CompanyName';
import { Link } from '../../../../../components/Link';
import { companyProfileUrlPath } from '../../../../../utils/routing/company-profile-url-path';
import { buildHiringSnapshotSeoRows } from '../utils/build-hiring-snapshot-seo-rows';
import type { DisplayJob } from '../../../../../types';

type HiringTrends = {
  roleDistribution: Record<string, number>;
  roleLevelDistribution?: Record<string, number>;
  locationDistribution?: Record<string, number>;
};

export interface CompanyCardProps {
  displayJob: DisplayJob;
  companyId?: string | null;
  panelColor: string;
  jobLocale: string;
  currentLocaleTrends?: HiringTrends;
}

const ROLE_LEVEL_COLORS = [0.85, 0.6, 0.4, 0.25, 0.14];

function formatFinancialValue(value: number, isMargin = false): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (isMargin) return `${sign}${(absValue * 100).toFixed(1)}%`;
  if (absValue >= 1_000_000) return `${sign}€${(absValue / 1_000_000).toFixed(1)}M`;
  if (absValue >= 1_000) return `${sign}€${(absValue / 1_000).toFixed(1)}K`;
  return `${sign}€${absValue.toFixed(0)}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  return { r: parseInt(h.substring(0, 2), 16), g: parseInt(h.substring(2, 4), 16), b: parseInt(h.substring(4, 6), 16) };
}

/**
 * Company summary card for the redesigned (V2) job detail page: logo/name/open-roles/top-locations,
 * a "Financial overview" block (conditional — only when the source job has financials), and
 * simplified always-visible "Most-hired roles" bar list + "Role-level mix" segmented bar
 * (replacing the tabbed donut charts, per the design's "always visible, no tabs" requirement).
 * Role/location distributions are derived the same way CompanySection's SEO tables already do
 * (buildHiringSnapshotSeoRows: sum = total, top-N sorted = top locations) — same data, new
 * presentation.
 */
export function CompanyCard({ displayJob, companyId, panelColor, jobLocale, currentLocaleTrends }: CompanyCardProps): React.ReactElement | null {
  const intl = useIntl();
  const companyDisplayName = displayJob.companyName ?? companyId ?? '';

  const locationRows = useMemo(() => buildHiringSnapshotSeoRows(currentLocaleTrends?.locationDistribution), [currentLocaleTrends]);
  const roleRows = useMemo(() => buildHiringSnapshotSeoRows(currentLocaleTrends?.roleDistribution), [currentLocaleTrends]);
  const roleLevelRows = useMemo(() => buildHiringSnapshotSeoRows(currentLocaleTrends?.roleLevelDistribution), [currentLocaleTrends]);

  const openRoles = locationRows.reduce((sum, row) => sum + row.activeListings, 0);
  const topLocations = locationRows.slice(0, 3);
  const otherLocationsCount = Math.max(0, locationRows.length - topLocations.length);

  const companyProfileHref = companyId ? companyProfileUrlPath(jobLocale as 'en' | 'fi' | 'sv', companyId) : '';

  const revenueNum = typeof displayJob.companyRevenue === 'string' ? parseFloat(displayJob.companyRevenue) : displayJob.companyRevenue;
  const profitNum = typeof displayJob.companyProfit === 'string' ? parseFloat(displayJob.companyProfit) : displayJob.companyProfit;
  const marginNum = typeof displayJob.companyProfitMargin === 'string' ? parseFloat(displayJob.companyProfitMargin) : displayJob.companyProfitMargin;
  const hasFinancials = [revenueNum, profitNum, marginNum].some((n) => typeof n === 'number' && Number.isFinite(n));

  const roleMax = Math.max(1, ...roleRows.slice(0, 5).map((r) => r.activeListings));
  const roleLevelTotal = roleLevelRows.reduce((sum, r) => sum + r.activeListings, 0);
  const rgb = hexToRgb(panelColor);
  let acc = 0;

  return (
    <div className="border-t border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pt-6">
      {hasFinancials ? (
        <div className="mb-6">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#8C8474] mb-3">
            <FormattedMessage id="jobDetailPageV2.financialOverview" defaultMessage="Financial overview" description="Section heading for company financials on the redesigned job detail page" />
          </div>
          <div className="flex flex-wrap gap-7">
            {typeof revenueNum === 'number' && Number.isFinite(revenueNum) ? (
              <div>
                <div className="[font-family:'Space_Grotesk',sans-serif] text-xl font-bold text-[#16150F] dark:text-[#F3EFE4]">{formatFinancialValue(revenueNum)}</div>
                <div className="mt-0.5 text-[11px] font-medium text-[#9A917F]">
                  <FormattedMessage id="jobDetailPageV2.revenue" defaultMessage="Revenue" description="Revenue stat label" />
                </div>
              </div>
            ) : null}
            {typeof profitNum === 'number' && Number.isFinite(profitNum) ? (
              <div>
                <div className={`[font-family:'Space_Grotesk',sans-serif] text-xl font-bold ${profitNum < 0 ? 'text-[#B3453D]' : 'text-[#16150F] dark:text-[#F3EFE4]'}`}>{formatFinancialValue(profitNum)}</div>
                <div className="mt-0.5 text-[11px] font-medium text-[#9A917F]">
                  <FormattedMessage id="jobDetailPageV2.profit" defaultMessage="Profit" description="Profit stat label" />
                </div>
              </div>
            ) : null}
            {typeof marginNum === 'number' && Number.isFinite(marginNum) ? (
              <div>
                <div className={`[font-family:'Space_Grotesk',sans-serif] text-xl font-bold ${marginNum < 0 ? 'text-[#B3453D]' : 'text-[#16150F] dark:text-[#F3EFE4]'}`}>{formatFinancialValue(marginNum, true)}</div>
                <div className="mt-0.5 text-[11px] font-medium text-[#9A917F]">
                  <FormattedMessage id="jobDetailPageV2.profitMargin" defaultMessage="Profit margin" description="Profit margin stat label" />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="flex items-center gap-3.5 mb-4">
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-white border border-[rgba(22,21,15,0.08)] p-1.5 box-border overflow-hidden">
          <CompanyLogo
            companyName={companyId || displayJob.companyName || ''}
            initialsSourceName={companyDisplayName}
            registryCompanyFormDescription={displayJob.registryCompanyFormDescription}
            isTyomarkkinatori={displayJob.isTyomarkkinatori}
            variant="rounded"
            size={44}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="[font-family:'Space_Grotesk',sans-serif] text-[13.5px] font-bold text-[#16150F] dark:text-[#F3EFE4]">
            <CompanyName name={companyDisplayName} /> {openRoles > 0 ? `· ${intl.formatMessage({ id: 'jobDetailPageV2.openRoles', defaultMessage: '{count} open roles', description: 'Company open roles count' }, { count: openRoles })}` : ''}
          </div>
          {topLocations.length > 0 ? (
            <div className="mt-0.5 text-[11.5px] text-[#8C8474] truncate">
              <FormattedMessage id="jobDetailPageV2.topLocations" defaultMessage="Top locations:" description="Label preceding a list of the company's top hiring locations" />{' '}
              {topLocations.map((row) => `${row.location} · ${row.activeListings}`).join('  ·  ')}
              {otherLocationsCount > 0
                ? intl.formatMessage(
                    { id: 'jobDetailPageV2.otherLocations', defaultMessage: ' +{count} other locations', description: 'Suffix noting additional hiring locations beyond the top ones shown' },
                    { count: otherLocationsCount },
                  )
                : ''}
            </div>
          ) : null}
        </div>
        {companyProfileHref ? (
          <Link
            href={companyProfileHref}
            locale={jobLocale}
            className="flex-none text-xs font-semibold text-[#16150F] dark:text-[#F3EFE4] border border-[rgba(22,21,15,0.15)] px-3.5 py-2 rounded-full no-underline whitespace-nowrap"
          >
            <FormattedMessage id="jobDetailPageV2.viewCompany" defaultMessage="View company" description="Link to the full company profile" />
          </Link>
        ) : null}
      </div>

      {roleRows.length > 0 || roleLevelRows.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
          {roleRows.length > 0 ? (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#8C8474] mb-2.5">
                <FormattedMessage id="jobDetailPageV2.mostHiredRoles" defaultMessage="Most-hired roles" description="Section heading for the company's most common job roles" />
              </div>
              <div className="flex flex-col gap-2">
                {roleRows.slice(0, 5).map((row) => (
                  <div key={row.location} className="flex items-center gap-2.5">
                    <span className="w-[110px] flex-none truncate text-xs font-medium text-[#4A4438] dark:text-[#C4A882]">{row.location}</span>
                    <div className="relative h-2 flex-1 rounded bg-[#F0EDE5] dark:bg-[#2A1D0F]">
                      <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${(row.activeListings / roleMax) * 100}%`, background: panelColor }} />
                    </div>
                    <span className="w-5 flex-none text-right text-xs font-semibold text-[#9A917F]">{row.activeListings}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {roleLevelRows.length > 0 ? (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#8C8474] mb-2.5">
                <FormattedMessage id="jobDetailPageV2.roleLevelMix" defaultMessage="Role-level mix" description="Section heading for the breakdown of seniority levels the company hires" />
              </div>
              <div className="relative flex h-3.5 overflow-hidden rounded-full bg-[#F0EDE5] dark:bg-[#2A1D0F]">
                {roleLevelRows.map((row, i) => {
                  const widthPct = (row.activeListings / roleLevelTotal) * 100;
                  const leftPct = acc;
                  acc += widthPct;
                  const opacity = ROLE_LEVEL_COLORS[i] ?? 0.1;
                  return (
                    <div
                      key={row.location}
                      className="absolute inset-y-0"
                      style={{ left: `${leftPct}%`, width: `${widthPct}%`, background: `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})` }}
                    />
                  );
                })}
              </div>
              <div className="mt-2.5 flex flex-wrap gap-x-3.5 gap-y-1.5">
                {roleLevelRows.map((row, i) => {
                  const opacity = ROLE_LEVEL_COLORS[i] ?? 0.1;
                  return (
                    <span key={row.location} className="flex items-center gap-1.5 text-[11px] font-medium text-[#8C8474]">
                      <span className="h-2 w-2 rounded-sm" style={{ background: `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})` }} />
                      {row.location} ({row.activeListings})
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
